/* eslint-disable react-hooks/exhaustive-deps */
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import PrimaryButton from "../../../../components/_utils/CommonButtons/PrimaryButton";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent";
import CardContainer from "../../../../components/_utils/CommonViewAllCard/CardContainer";
import TextWithBank from "../../../../components/_utils/CommonViewAllCard/CardSubComponent/TextWithBank";
import TextWithIcon from "../../../../components/_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import PaginationComponent from "../../../../components/_utils/PaginationComponent";
import Loader from "../../../../components/Loader/Index";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { getAllCategories } from "../../../../redux/features/sharedResources/sharedResources";
import { bulkTransactionsViewAll } from "../../../../redux/features/transactions/beneficiaryTransactions/index.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";
import useCategories from "../../../../utils/useCategories";
import useCustomSearchParams from "../../../../utils/useCustomSearchParams";
import UseHandleScroll from "../../../../hooks/useHandleScroll";

const convertDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const timestamp = Math.floor(
    date.getTime() / IntervalConfiguration.intervalMedium,
  );
  return timestamp;
};

const formatDateForAPI = (date) => {
  const dateObj = new Date(date * 1000);
  return format(dateObj, "dd-MM-yyyy");
};

const BulkTransactionViewAll = ({ popType, categoryPass }) => {
  const navigate = useNavigate();
  const categoryMapper = useCategories();
  const dispatch = useDispatch();
  const dropdownOptions = PaginationConfiguration.paginationOptions;
  const { searchParam, handleSearchParams, handleClearSearchParam } =
    useCustomSearchParams({
      pageno: GlobalConfiguration.lengthCheckOne,
      pagesize: dropdownOptions[0],
    });
  const [, setIsOpen] = useState(false);
  const [active, setActive] = useState("");
  const [mainData, setMainData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState([]);
  const [viewAllData, setViewAllData] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [searchUserTextDebounce, setSearchUserTextDebounce] = useState("");
  const [txnSearchType, setTxnSearchType] = useState(
    searchParam.get("search") || "",
  );
  const [currentStatus, setCurrentStatus] = useState(
    searchParam.get("status") || "",
  );
  const [currentCategory, setCurrentCategory] = useState(
    categoryPass
      ? categoryPass?.category_id
      : searchParam.get("category") || "",
  );
  const [displayCategory, setDisplayCategory] = useState(
    categoryPass
      ? categoryPass?.category_name
      : searchParam.get("dispCategory") || "",
  );
  const [pageSize, setPageSize] = useState(
    searchParam.get("pagesize") || dropdownOptions[0],
  );
  const [selectedValue, setSelectedValue] = useState(
    searchParam.get("pagesize") || dropdownOptions[0],
  );
  const [currentPage, setCurrentPage] = useState(
    searchParam.get("pageno") || GlobalConfiguration.lengthCheckOne,
  );
  const [startDateFilter, setStartDateFilter] = useState(
    convertDate(searchParam.get("date")?.split(",")?.[0]) || "",
  );
  const [endDateFilter, setEndDateFilter] = useState(
    convertDate(searchParam.get("date")?.split(",")?.[1]) || "",
  );
  const categories = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );
  const [direction, setDirection] = useState("");
  const lastScrollTop = useRef(GlobalConfiguration.lengthCheckZero);
  const { handleScroll } = UseHandleScroll(
    setDirection,
    lastScrollTop,
    GlobalConfiguration.bulkTxnCardHeight,
  );

  const { markerData } = useSelector((s) => s.multiEntitySwitcherSlice);

  const getStatusColor = (status) => {
    switch (status) {
      case "failed":
        return "var(--failed)";
      case "processed":
        return "var(--settled)";
      case "processing":
      case "created":
        return "var(--queued)";
      default:
        return "var(--text-primary)";
    }
  };

  const handleSearch = (text) => {
    setSearchUserTextDebounce(text);
  };

  const handleClearParams = useCallback(
    (e) => {
      handleClearSearchParam();
      e?.stopPropagation();
      setCurrentCategory(categoryPass ? categoryPass?.category_id : "");

      setStartDateFilter("");
      setEndDateFilter("");

      setTxnSearchType("");
      setSearchUserTextDebounce("");
      setCurrentStatus("");
      setShowSearch(false);
    },
    [setShowSearch, setTxnSearchType],
  );

  const handleSubmit = (key, value) => {
    if (popType !== "BulkUploadDetails") {
      handleSearchParams(key, value);
    }
  };

  const handleSelect = useCallback((value) => {
    handleSubmit("pagesize", value);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);

    setSelectedValue(value);
    setPageSize(value);
  }, []);

  const handlePagination = useCallback((page) => {
    handleSubmit("pageno", page);
    setCurrentPage(page);
  }, []);

  const handleDateChange = (date) => {
    if (date) {
      const [start, end] = date;
      setStartDateFilter(convertDate(start));
      setEndDateFilter(convertDate(end));
    } else {
      setStartDateFilter("");
      setEndDateFilter("");
    }
  };

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate],
  );

  const hasActiveFilters = useMemo(
    () => currentCategory !== "" || startDateFilter || endDateFilter,
    [currentCategory, startDateFilter, endDateFilter],
  );

  const containerClassName = `${popType === "BulkUploadDetails" ? "h-full" : "h-screen"} overflow-y-hidden`;

  const contentClassName = useMemo(
    () =>
      `w-full h-screen ${
        popType === "BulkUploadDetails" ? "h-full flex-1" : ""
      } mb-[var(--pagination-height)] mx-auto ${
        popType === "BulkUploadDetails" ? "" : "h-screen"
      }`,
    [showSearch, showInnerSearchBar],
  );

  const fetchFilterData = async () => {
    let response;
    let data = [];

    setLoadingData(true);

    switch (active) {
      case "Category":
        response = await dispatch(
          getAllCategories({ search_string: searchUserTextDebounce }),
        );

        data =
          response?.payload?.categories?.map((itm) => ({
            id: itm.id,
            Category: itm.name,
            Description: itm?.description || "Not given",
          })) || [];

        setInputData(data);
        setLoadingData(false);
        break;

      case "Search":
        setTxnSearchType(searchUserTextDebounce);
        setLoadingData(false);
        break;

      default:
        break;
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      let queryParams = {
        page_no: currentPage,
        page_item_count: pageSize,
      };

      if (currentStatus && currentStatus !== "all") {
        queryParams.currentStatus = currentStatus;
      }
      if (currentCategory && currentCategory !== "") {
        queryParams.initCategory = currentCategory;
      }
      if (txnSearchType !== "") {
        queryParams.file_name = txnSearchType;
      }
      if (
        startDateFilter &&
        endDateFilter &&
        !isNaN(Number(startDateFilter)) &&
        !isNaN(Number(endDateFilter))
      ) {
        queryParams.start_date = formatDateForAPI(startDateFilter);
        queryParams.end_date = formatDateForAPI(endDateFilter);
      }

      const response = await dispatch(bulkTransactionsViewAll(queryParams));

      setMainData(response?.payload);
      const newData = response?.payload?.transactions_file || [];
      setViewAllData(newData);
    } catch (error) {
      setViewAllData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    dispatch,
    currentPage,
    pageSize,
    currentStatus,
    currentCategory,
    startDateFilter,
    endDateFilter,
    txnSearchType,
    markerData,
  ]);

  useEffect(() => {
    dispatch(enableSwitcher());
    return () => {
      dispatch(disableSwitcher());
    };
  }, [markerData]);

  useEffect(() => {
    if (active?.length) {
      fetchFilterData();
    }
  }, [active]);

  return (
    <div className={containerClassName}>
      <div className="h-full flex flex-col overflow-hidden">
        <div
          className={`hidden Qmd:flex bg-primary-nav ${showInnerSearchBar && "relative z-z-index-overlay"} ${popType === "BulkUploadDetails" ? "" : "min-h-navbar-breadcrumb-height"}`}
        ></div>
        <div className="relative z-z-index-overlay">
          <TopNavComponent
            direction={direction}
            filteredSearchProps={{
              setShowInnerSearchBar: setShowInnerSearchBar,
              showSearchBar: showSearch,
              isSearchDisabled: loading,
              onSearchInput: handleSearch,
              searchText: searchUserTextDebounce,
              searchPlaceholder: "Search bulk transactions",
              filterPlaceholder: "File name",
              filterItems: [
                {
                  name: "Search",
                  isMulti: false,
                  value: txnSearchType,
                  setValue: (val) => {
                    handleSubmit("search", val);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    setTxnSearchType(val);
                  },
                  isDropdown: false,
                  isDisabled: loading,
                },
                {
                  name: "Category",
                  valueType: "string",
                  short_name: "Cat",
                  isMulti: false,
                  value: currentCategory,
                  displayValue: displayCategory,
                  setValue: (val) => {
                    const c =
                      categories.filter((x) => x.id === val)[0]?.name ?? "";
                    setDisplayCategory(c);
                    handleSubmit("category", val);
                    handleSubmit("dispCategory", c);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    setCurrentCategory(val);
                  },
                  isDropdown: true,
                  isDisabled: loading
                    ? true
                    : popType === "BulkUploadDetails"
                      ? true
                      : false,
                },
              ],
              filters: [
                {
                  name: "File status",
                  filterType: "dropdown",
                  variant: "single",
                  size: "small",
                  value: currentStatus,
                  dropdownPlaceHolder: "Select status",
                  isDisable: false,
                  options: ["processed", "processing", "failed"],
                  statusPop: true,
                  handleChange: (val) => {
                    handleSubmit("status", val);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    setCurrentStatus(val);
                  },
                },
                {
                  name: "Date range",
                  filterType: "calender",
                  variant: "range",
                  size: "small",
                  value: startDateFilter && endDateFilter,
                  isDisable: false,
                  startDate: startDateFilter
                    ? new Date(
                        startDateFilter * IntervalConfiguration.intervalMedium,
                      )
                    : startDateFilter,
                  endDate: endDateFilter
                    ? new Date(
                        endDateFilter * IntervalConfiguration.intervalMedium,
                      )
                    : endDateFilter,
                  handleChange: (val) => {
                    handleSubmit("date", val);
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    handleDateChange(val);
                  },
                },
              ],

              isFilterClearAllDisabled: !(
                (startDateFilter && endDateFilter) ||
                currentStatus !== "" ||
                loading
              ),

              isSearchClearAllDisabled:
                popType === "BulkUploadDetails"
                  ? !txnSearchType?.length
                  : !(currentCategory !== "" || txnSearchType?.length),

              inputData: inputData,
              active: active,
              setActive: setActive,
              loadingData: loadingData,
              loading: loading,

              isSortDisabled: loading,
              isFilterDisabled: loading,
              setLoading: setLoadingData,
              isShowFilter: true,
            }}
            BottomHeader={"Post upload process"}
            BottomSubText={
              "Upon successful verification, transactions will be created and sent for approval. If transaction creation is unsuccessful, fix the errors, and upload the corrected data as a new file."
            }
          />
        </div>

        {showInnerSearchBar && (
          <div className="absolute bg-PopUp-BackDrop bg-opacity-light-opacity w-full h-full z-z-index-header"></div>
        )}
        <div
          className={`${contentClassName} flex flex-col gap-gap-md w-width-full overflow-y-auto`}
          style={{
            scrollPaddingTop: "var(--padding-md)",
            scrollPaddingBottom: "var(--padding-md)",
          }}
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="loader-container m-auto justify-center items-center flex w-full h-full">
              <Loader />
            </div>
          ) : viewAllData?.length > GlobalConfiguration.lengthCheckZero ? (
            <div className="standard-card-alignment gap-x-gap-sm gap-y-gap-md w-width-full">
              {viewAllData?.map((transaction) => (
                <CardContainer
                  key={transaction?.id}
                  context={transaction}
                  isSelected={false}
                  isDisabled={false}
                  onClickHandler={() =>
                    handleNavigate(
                      `/beneficiary_transaction_dashboard/bulk_transaction_view_all/bulk_transaction_detail_page/${transaction?.id}`,
                    )
                  }
                >
                  <card-body>
                    <TextWithIcon
                      type={"text"}
                      fontWeight={"normal"}
                      text={transaction?.file_name}
                      subText={"File name"}
                      textColor={"var(--text-primary)"}
                    />
                    <TextWithBank
                      type={"text"}
                      fontWeight={"semibold"}
                      text={
                        transaction?.file_status?.charAt(0)?.toUpperCase() +
                        transaction?.file_status?.slice(1)
                      }
                      subText={"Status"}
                      textColor={getStatusColor(transaction?.file_status)}
                      src={categoryMapper(transaction.category_id).url}
                      imgColor={categoryMapper(transaction?.category_id).colour}
                    />
                    <TextWithIcon
                      type={"text"}
                      fontWeight={"normal"}
                      text={transaction?.uploaded_by}
                      subText={"Uploaded by"}
                      textColor={"var(--text-primary)"}
                    />
                  </card-body>
                </CardContainer>
              ))}
            </div>
          ) : viewAllData?.length === GlobalConfiguration.lengthCheckZero &&
            (hasActiveFilters || currentStatus !== "" || txnSearchType) ? (
            <div className="w-full flex flex-col items-center justify-center text-center gap-gap-sm m-auto">
              <div>
                <img
                  src={ICONS?.NO_DATA_FOUND}
                  alt="no data found"
                />
              </div>
              <p className="text-sm text-text-primary">No data found.</p>
              <PrimaryButton
                enabledBgColor="var(--primary-nav-act)"
                btnText="Clear parameters"
                hoverOption="white"
                btnType="button"
                handleFunction={handleClearParams}
                enabledTextColor="var(--text-primary)"
              />
            </div>
          ) : (
            <div className="w-full flex flex-col items-center justify-center text-center gap-gap-sm m-auto">
              <div>
                <img
                  src={ICONS?.NO_PENDING_TRANSACTION}
                  alt="no_pending_transaction"
                />
              </div>
              <p className="text-sm text-text-primary">
                There are no more transactions that require <br /> your
                approval.
              </p>
              <PrimaryButton
                enabledBgColor="var(--primary-nav-act)"
                btnText="Beneficiary transaction dashboard"
                hoverOption="white"
                btnType="button"
                handleFunction={() =>
                  navigate("/beneficiary_transaction_dashboard")
                }
                enabledTextColor="var(--text-primary)"
              />
            </div>
          )}
          <div
            style={{
              zIndex: "var(--z-index-modal)",
              width: "var(--width-full)",
              bottom: "var(--bottom-base)",
            }}
            className={`${showInnerSearchBar && " pointer-events-none opacity-dark-opacity"} ${loading || viewAllData?.length === GlobalConfiguration.lengthCheckZero ? "hidden" : "block"} ${popType === "BulkUploadDetails" ? " absolute pb-padding-base" : " fixed"}`}
          >
            {showInnerSearchBar && (
              <div className="absolute bg-PopUp-BackDrop bg-opacity-light-opacity w-full h-full z-z-index-header"></div>
            )}
            <div>
              <PaginationComponent
                handleSelect={handleSelect}
                selectedValue={selectedValue}
                data={mainData}
                pageSize={pageSize}
                dropdownOptions={dropdownOptions}
                handlePagination={handlePagination}
                currentPage={currentPage}
                setIsOpen={setIsOpen}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkTransactionViewAll;
